import { useRef, useState } from 'react';
import '../styles/hittakompetens.css';
import { getEmailJsTemplateId, sendEmailJsForm } from '../utils/emailjsClient';
import ReCAPTCHA from 'react-google-recaptcha';
import { getRecaptchaSiteKey } from '../utils/recaptcha';
import { useRecaptchaGate } from '../hooks/useRecaptchaGate';

const pageCopy = {
  swe: {
    heroTitle: 'Hitta kompetens snabbare och mer träffsäker',
    heroText:
      'Vi hjälper er att tillsätta specialister inom bland annat IT, teknik, ingenjörskap och ekonomi med tydlig process, hög kvalitet och snabb leverans.',
    heroPrimary: 'Skicka förfrågan',
    heroSecondary: 'Kontakta oss direkt',
    highlights: [
      { title: 'Svar inom 24h', text: 'Ni får snabb återkoppling och en konkret nästa aktivitet.' },
      { title: 'Kvalitativt urval', text: 'Vi presenterar kandidater som matchar både kravprofil och kultur.' },
      { title: 'Flexibla upplagg', text: 'Interim, bemanning eller rekrytering beroende på ert behov.' }
    ],
    processTitle: 'Så fungerar det',
    process: [
      { step: '01', title: 'Behovsgenomgång', text: 'Vi kartlägger kompetens, tidsram, budget och leveransmål.' },
      { step: '02', title: 'Urval och kvalitet', text: 'Vi validerar kandidater genom intervju, referenser och matchning.' },
      { step: '03', title: 'Presentation', text: 'Ni får ett fokuserat urval med tydlig motivering per kandidat.' },
      { step: '04', title: 'Start och uppföljning', text: 'Vi stöttar onboarding och följer upp kvalitet efter start.' }
    ],
    formTitle: 'Berätta vilket kompetensbehov ni har',
    formText: 'Fyll i uppgifterna så kontaktar vi er med ett förslag på upplägg.',
    labels: {
      company: 'Företag *',
      contactName: 'Kontaktperson *',
      email: 'E-post *',
      phone: 'Telefon *',
      competence: 'Typ av kompetens *',
      startDate: 'När behovet startar *',
      duration: 'Hur länge behövs kompetensen? *',
      scope: 'Omfattning (%) *',
      workModel: 'Arbetsmodell *',
      message: 'Beskriv behovet (mål, team, teknik, förväntad leverans) *'
    },
    placeholders: {
      company: 'Exempel AB',
      contactName: 'Namn Efternamn',
      email: 'namn@företag.se',
      phone: '070-123 45 67',
      message: 'Vi söker en senior .NET-utvecklare för ett projekt som startar i maj...'
    },
    options: {
      competence: [
        { value: '', label: 'Välj kompetens' },
        { value: 'it-dev', label: 'Systemutveckling / IT' },
        { value: 'devops', label: 'DevOps / Cloud' },
        { value: 'finance', label: 'Ekonomi / Controlling' },
        { value: 'project', label: 'Projektledning / Produkt' },
        { value: 'other', label: 'Annan specialistkompetens' }
      ],
      duration: [
        { value: '', label: 'Välj tidslängd' },
        { value: '0-3', label: '0-3 månader' },
        { value: '3-6', label: '3-6 månader' },
        { value: '6-12', label: '6-12 månader' },
        { value: '12+', label: '12+ månader' }
      ],
      scope: [
        { value: '', label: 'Välj omfattning' },
        { value: '25', label: '25%' },
        { value: '50', label: '50%' },
        { value: '75', label: '75%' },
        { value: '100', label: '100%' }
      ],
      workModel: [
        { value: '', label: 'Välj arbetsmodell' },
        { value: 'remote', label: 'Remote' },
        { value: 'hybrid', label: 'Hybrid' },
        { value: 'onsite', label: 'On-site' }
      ]
    },
    submit: 'Skicka förfrågan',
    success: 'Tack! Vi har tagit emot er förfrågan och återkommer inom kort.',
    helper: 'Har ni ett akut behov? Vi kan ofta presentera profiler redan inom 24-72 timmar.',
    recaptchaHint: 'Fyll i alla obligatoriska fält ovan för att kunna använda reCAPTCHA.'
  },
  eng: {
    heroTitle: 'Find the right competence faster',
    heroText:
      'We help you secure specialists in IT, technology, engineering, and finance through a clear process, high quality, and fast delivery.',
    heroPrimary: 'Send request',
    heroSecondary: 'Contact us directly',
    highlights: [
      { title: 'Response within 24h', text: 'You get a clear next step quickly.' },
      { title: 'Curated selection', text: 'We present candidates matched on both skills and team fit.' },
      { title: 'Flexible setup', text: 'Interim, consulting, or recruitment based on your need.' }
    ],
    processTitle: 'How it works',
    process: [
      { step: '01', title: 'Need discovery', text: 'We map required skills, timeline, budget, and delivery goals.' },
      { step: '02', title: 'Selection quality', text: 'We validate candidates through interviews, references, and fit.' },
      { step: '03', title: 'Shortlist delivery', text: 'You get a focused shortlist with clear rationale per profile.' },
      { step: '04', title: 'Start and follow-up', text: 'We support onboarding and follow up after start.' }
    ],
    formTitle: 'Tell us what competence you need',
    formText: 'Share your requirements and we will propose the right setup.',
    labels: {
      company: 'Company *',
      contactName: 'Contact person *',
      email: 'Email *',
      phone: 'Phone *',
      competence: 'Type of competence *',
      startDate: 'When the need starts *',
      duration: 'For how long? *',
      scope: 'Scope (%) *',
      workModel: 'Work model *',
      message: 'Describe your need (goals, team, tech, expected outcome) *'
    },
    placeholders: {
      company: 'Example AB',
      contactName: 'Name Surname',
      email: 'name@company.com',
      phone: '+46 70 123 45 67',
      message: 'We need a senior .NET developer for a project starting in May...'
    },
    options: {
      competence: [
        { value: '', label: 'Select competence' },
        { value: 'it-dev', label: 'Software development / IT' },
        { value: 'devops', label: 'DevOps / Cloud' },
        { value: 'finance', label: 'Finance / Controlling' },
        { value: 'project', label: 'Project management / Product' },
        { value: 'other', label: 'Other specialist competence' }
      ],
      duration: [
        { value: '', label: 'Select duration' },
        { value: '0-3', label: '0-3 months' },
        { value: '3-6', label: '3-6 months' },
        { value: '6-12', label: '6-12 months' },
        { value: '12+', label: '12+ months' }
      ],
      scope: [
        { value: '', label: 'Select scope' },
        { value: '25', label: '25%' },
        { value: '50', label: '50%' },
        { value: '75', label: '75%' },
        { value: '100', label: '100%' }
      ],
      workModel: [
        { value: '', label: 'Select work model' },
        { value: 'remote', label: 'Remote' },
        { value: 'hybrid', label: 'Hybrid' },
        { value: 'onsite', label: 'On-site' }
      ]
    },
    submit: 'Send request',
    success: 'Thank you! We have received your request and will get back to you shortly.',
    helper: 'Urgent need? We can often present profiles within 24-72 hours.',
    recaptchaHint: 'Complete all required fields above to use reCAPTCHA.'
  }
};

function HittaKompetens({ lang = 'swe', setPage }) {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const text = pageCopy[lang] || pageCopy.swe;
  const formRef = useRef(null);
  const captchaRef = useRef(null);
  const [recaptchaAllowed, syncRecaptchaGate] = useRecaptchaGate(formRef, captchaRef, setCaptchaToken);
  const captchaExpiredMessage = lang === 'swe'
    ? 'Verifieringen gick ut. Bekräfta att du inte är en robot igen.'
    : 'Verification expired. Please confirm you are not a robot again.';
  const captchaErrorMessage = lang === 'swe'
    ? 'reCAPTCHA kunde inte laddas klart. Ladda om sidan och försök igen.'
    : 'reCAPTCHA could not finish loading. Refresh the page and try again.';

  const handleCaptchaExpired = () => {
    setCaptchaToken('');
    if (status === 'sending') {
      setStatus('idle');
    }
    setError(captchaExpiredMessage);
  };

  const handleCaptchaErrored = () => {
    setCaptchaToken('');
    if (status === 'sending') {
      setStatus('idle');
    }
    setError(captchaErrorMessage);
    captchaRef.current?.reset?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const formEl = formRef.current;
    if (!formEl) {
      return;
    }
    if (!formEl.checkValidity()) {
      formEl.reportValidity();
      return;
    }

    setStatus('sending');

    try {
      const siteKey = getRecaptchaSiteKey();
      const token = captchaRef.current?.getValue?.() || captchaToken;
      if (siteKey && !token) {
        setStatus('idle');
        setError(lang === 'swe' ? 'Bekräfta att du inte är en robot.' : 'Please confirm you are not a robot.');
        return;
      }

      const timeEl = formEl.querySelector('input[name="time"]');
      if (timeEl) {
        timeEl.value = new Date().toLocaleString(lang === 'swe' ? 'sv-SE' : 'en-GB', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }

      const getSelectLabel = (name) =>
        formEl.querySelector(`select[name="${name}"]`)?.selectedOptions?.[0]?.textContent?.trim() || '';

      const detailsEl = formEl.querySelector('input[name="details"]');
      if (detailsEl) {
        const contactName = formEl.querySelector('input[name="contactName"]')?.value?.trim() || '';
        const competence = getSelectLabel('competence');
        const startDate = formEl.querySelector('input[name="startDate"]')?.value || '';
        const duration = getSelectLabel('duration');
        const scope = getSelectLabel('scope');
        const workModel = getSelectLabel('workModel');
        const message = formEl.querySelector('textarea[name="message"]')?.value?.trim() || '';

        const rows = [];
        if (contactName) rows.push(`${lang === 'swe' ? 'Kontaktperson' : 'Contact person'}: ${contactName}`);
        if (competence) rows.push(`${lang === 'swe' ? 'Kompetens' : 'Competence'}: ${competence}`);
        if (startDate) rows.push(`${lang === 'swe' ? 'Start' : 'Start'}: ${startDate}`);
        if (duration) rows.push(`${lang === 'swe' ? 'Varaktighet' : 'Duration'}: ${duration}`);
        if (scope) rows.push(`${lang === 'swe' ? 'Omfattning' : 'Scope'}: ${scope}`);
        if (workModel) rows.push(`${lang === 'swe' ? 'Arbetsmodell' : 'Work model'}: ${workModel}`);
        if (message) {
          rows.push('');
          rows.push(message);
        }

        detailsEl.value = rows.join('\n');
      }

      await sendEmailJsForm(formEl, getEmailJsTemplateId('competence'));
      setStatus('sent');
      formEl.reset();
      syncRecaptchaGate();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('EmailJS send failed (competence)', err);
      setStatus('idle');
      setError(lang === 'swe' ? 'Kunde inte skicka just nu. Försök igen.' : 'Could not send right now. Please try again.');
    }
  };

  return (
    <section className="competence-page">
      <header className="competence-hero">
        <p className="competence-kicker">Execora Talent Match</p>
        <h1>{text.heroTitle}</h1>
        <p>{text.heroText}</p>
        <div className="competence-hero-actions">
          <button type="button" className="competence-primary" onClick={() => document.getElementById('competence-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
            {text.heroPrimary}
          </button>
          <button type="button" className="competence-secondary" onClick={() => setPage('contact')}>
            {text.heroSecondary}
          </button>
        </div>
      </header>

      <section className="competence-highlights">
        {text.highlights.map((item) => (
          <article key={item.title} className="competence-highlight-card">
            <h2>{item.title}</h2>
            <p>{item.text}</p>
          </article>
        ))}
      </section>

      <section className="competence-process" aria-label={text.processTitle}>
        <div className="competence-process-header">
          <h2>{text.processTitle}</h2>
          <p>{text.helper}</p>
        </div>
        <div className="competence-process-grid">
          {text.process.map((item) => (
            <article key={item.step} className="competence-step-card">
              <span className="competence-step-number">{item.step}</span>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="competence-request" id="competence-form">
        <div className="competence-request-head">
          <h2>{text.formTitle}</h2>
          <p>{text.formText}</p>
        </div>

        <form
          ref={formRef}
          className="competence-form"
          onSubmit={handleSubmit}
          onInput={syncRecaptchaGate}
          onChange={syncRecaptchaGate}
        >
          <input type="hidden" name="source" value="competence" />
          <input type="hidden" name="time" value="" />
          <input type="hidden" name="context_title" value={lang === 'swe' ? 'Förfrågan: Hitta kompetens' : 'Inquiry: Find competence'} />
          <input type="hidden" name="details" value="" />
          <label>
            <span>{text.labels.company}</span>
            <input type="text" name="company" placeholder={text.placeholders.company} required />
          </label>
          <label>
            <span>{text.labels.contactName}</span>
            <input type="text" name="contactName" placeholder={text.placeholders.contactName} required />
          </label>
          <label>
            <span>{text.labels.email}</span>
            <input type="email" name="email" placeholder={text.placeholders.email} required />
          </label>
          <label>
            <span>{text.labels.phone}</span>
            <input type="tel" name="phone" placeholder={text.placeholders.phone} required />
          </label>

          <label>
            <span>{text.labels.competence}</span>
            <select name="competence" defaultValue="" required>
              {text.options.competence.map((option) => (
                <option key={option.value || 'placeholder'} value={option.value} disabled={option.value === ''}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>{text.labels.startDate}</span>
            <input type="date" name="startDate" required />
          </label>
          <label>
            <span>{text.labels.duration}</span>
            <select name="duration" defaultValue="" required>
              {text.options.duration.map((option) => (
                <option key={option.value || 'placeholder'} value={option.value} disabled={option.value === ''}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>{text.labels.scope}</span>
            <select name="scope" defaultValue="" required>
              {text.options.scope.map((option) => (
                <option key={option.value || 'placeholder'} value={option.value} disabled={option.value === ''}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="competence-form-full">
            <span>{text.labels.workModel}</span>
            <select name="workModel" defaultValue="" required>
              {text.options.workModel.map((option) => (
                <option key={option.value || 'placeholder'} value={option.value} disabled={option.value === ''}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="competence-form-full">
            <span>{text.labels.message}</span>
            <textarea name="message" rows="6" placeholder={text.placeholders.message} required />
          </label>

          {getRecaptchaSiteKey() && (
            <>
              <div
                className={`recaptcha-wrap${recaptchaAllowed ? '' : ' recaptcha-wrap--gated'}`}
                aria-disabled={!recaptchaAllowed}
              >
                <ReCAPTCHA
                  ref={captchaRef}
                  sitekey={getRecaptchaSiteKey()}
                  onChange={(token) => setCaptchaToken(token || '')}
                  onExpired={handleCaptchaExpired}
                  onErrored={handleCaptchaErrored}
                  hl={lang === 'swe' ? 'sv' : 'en'}
                />
              </div>
              {!recaptchaAllowed && (
                <p className="recaptcha-hint" role="status">
                  {text.recaptchaHint}
                </p>
              )}
            </>
          )}

          <button type="submit" className="competence-submit" disabled={status === 'sending'}>
            {status === 'sending' ? (lang === 'swe' ? 'Skickar…' : 'Sending…') : text.submit}
          </button>
        </form>

        {error && <p className="competence-success">{error}</p>}
        {status === 'sent' && !error && <p className="competence-success">{text.success}</p>}
      </section>
    </section>
  );
}

export default HittaKompetens;